import React from 'react';
import { FingerConfig, SessionStats, TrainingMode, ComplexityMode } from '@/types';
import { ComplexityToggle } from '@/components/UI/ComplexityToggle';
import CourseDayStats from './CourseDayStats';

const ONE_WORD_PRESETS = [
  'as', 'dad', 'sad', 'fad', 'gas', 'had', 'lag', 'glass',
  'pop', 'popup', 'quiet', 'pretty',
  'ban', 'man', 'van', 'cab',
  'space', 'banana', 'rhythm', 'minimum',
];

interface UnifiedCourseWidgetProps {
  currentLevel: number;
  currentDay: number;
  currentRound: number;
  courseProgress: any; // CourseProgress or DayProgress[]
  fingerConfig: FingerConfig;
  description: string;
  onDaySelect: (levelNumber: number, dayNumber: number) => void;
  onRoundSelect: (levelNumber: number, roundNumber: number) => void;
  onStartTraining: () => void;
  className?: string;
  customMode?: boolean;
  customStats?: SessionStats;
  onModeChange?: (mode: TrainingMode) => void;
  currentMode?: TrainingMode;
  complexityMode?: ComplexityMode;
  onComplexityChange?: (mode: ComplexityMode) => void;
  onRequestLogin?: () => void;
  isAuthenticated?: boolean;
  uid?: string;
  onBack?: () => void;
  oneWordValue?: string;
  onOneWordChange?: (val: string) => void;
}

export const UnifiedCourseWidget: React.FC<UnifiedCourseWidgetProps> = ({
  currentLevel,
  currentDay,
  currentRound,
  courseProgress,
  fingerConfig,
  onDaySelect,
  onRoundSelect,
  onStartTraining,
  className = '',
  customMode = false,
  customStats,
  onModeChange,
  currentMode = 'custom',
  complexityMode = 1,
  onComplexityChange,
  onRequestLogin,
  isAuthenticated,
  oneWordValue = '',
  onOneWordChange,
}) => {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const isOneWordValid = oneWordValue.trim().length >= 2;
  // Widget now manages which level it is actively viewing
  const [viewLevel, setViewLevel] = React.useState(currentLevel);

  // Keep viewLevel in sync with currentLevel if the external source changes it
  React.useEffect(() => {
    setViewLevel(currentLevel);
  }, [currentLevel]);

  // Determine the effective progress to show for the currently viewed level
  const effectiveCourseProgress = Array.isArray(courseProgress)
    ? courseProgress // Legacy fallback
    : (courseProgress?.levelProgress?.[viewLevel]?.dayProgress || []);
  // Calendar functionality - UPDATED to match round logic
  // 🔧 FIXED: Enhanced debugging for unlocked days logic
  const getCalendarDay = (dayNumber: number) => {
    const dayProgress = effectiveCourseProgress.find((dp: any) => dp.dayNumber === dayNumber);

    console.log(`🔍 Day ${dayNumber} analysis:`, {
      dayProgress: !!dayProgress,
      unlocked: dayProgress?.unlocked,
      completed: dayProgress?.completed,
      currentDay,
      globalUnlockedDays: effectiveCourseProgress.filter((dp: any) => dp.unlocked).map((dp: any) => dp.dayNumber)
    });

    if (!dayProgress) {
      console.log(`🔍 Day ${dayNumber}: No progress found - LOCKED`);
      return {
        dayNumber,
        state: 'locked' as const,
        rounds: { completed: 0, total: 5 },
        isSelectable: false
      };
    }

    const completedRounds = dayProgress.rounds.filter((r: any) => r.completed).length;

    let state: 'locked' | 'unlocked' | 'current' | 'completed';
    let isSelectable = false;

    if (!dayProgress.unlocked) {
      // Day is locked - can't access
      state = 'locked';
      isSelectable = false;
      console.log(`🔒 Day ${dayNumber}: LOCKED (unlocked=false)`);
    } else if (dayProgress.completed) {
      // Day is completed - can go back to it (GREEN)
      state = 'completed';
      isSelectable = true;
      console.log(`✅ Day ${dayNumber}: COMPLETED (can return)`);
    } else if (dayNumber === currentDay) {
      // This is the current day - selected (BLUE + BORDER)
      state = 'current';
      isSelectable = true;
      console.log(`🎯 Day ${dayNumber}: CURRENT (active)`);
    } else {
      // Day is unlocked but not current - available (BLUE)
      state = 'unlocked';
      isSelectable = true;
      console.log(`🔓 Day ${dayNumber}: UNLOCKED (available)`);
    }

    return {
      dayNumber,
      state,
      rounds: { completed: completedRounds, total: 5 },
      isSelectable
    };
  };

  const handleDayClick = (dayNumber: number) => {
    const calendarDay = getCalendarDay(dayNumber);
    console.log(`🎯 Day ${dayNumber} clicked:`, {
      state: calendarDay.state,
      isSelectable: calendarDay.isSelectable,
      currentDay,
      dayProgress: effectiveCourseProgress.find((dp: any) => dp.dayNumber === dayNumber)
    });

    // Only allow clicking on selectable days (completed, current, or unlocked)
    if (calendarDay.isSelectable) {
      console.log(`✅ Day ${dayNumber} selection allowed - calling onDaySelect`);
      onDaySelect(viewLevel, dayNumber);
    } else {
      console.log(`❌ Day ${dayNumber} selection blocked - not selectable`);
    }
  };

  // Round selector functionality
  const getCurrentDayRounds = () => {
    const dayProgress = effectiveCourseProgress.find((dp: any) => dp.dayNumber === currentDay);
    return dayProgress?.rounds || [];
  };

  const getRoundState = (roundNumber: number): 'completed' | 'current' | 'locked' => {
    const rounds = getCurrentDayRounds();
    const roundProgress = rounds.find((r: any) => r.roundNumber === roundNumber);

    // If this round is completed, it's completed
    if (roundProgress?.completed) {
      return 'completed';
    }

    // If this is the current round, it's current
    if (roundNumber === currentRound) {
      return 'current';
    }

    // For new users or users without progress, only round 1 should be available
    if (rounds.length === 0) {
      return roundNumber === 1 ? 'current' : 'locked';
    }

    // For rounds beyond the first, check if previous round is completed
    if (roundNumber > 1) {
      const previousRound = rounds.find((r: any) => r.roundNumber === roundNumber - 1);
      if (previousRound?.completed) {
        return 'current';
      } else {
        return 'locked';
      }
    }

    // Round 1 is always current if we get here
    return 'current';
  };

  const isRoundSelectable = (roundNumber: number): boolean => {
    const state = getRoundState(roundNumber);
    return state === 'completed' || state === 'current';
  };

  const handleRoundClick = (roundNumber: number) => {
    // We allow clicking the current round if we are looking at a different level than the current one
    if (roundNumber === currentRound && viewLevel === currentLevel) return;
    if (isRoundSelectable(roundNumber)) {
      onRoundSelect(viewLevel, roundNumber);
    }
  };

  // Configuration display functionality
  const getFingerNames = (): string => {
    const allFingers = [...fingerConfig.activeFingersLeft, ...fingerConfig.activeFingersRight];

    if (allFingers.length === 0) {
      return 'None';
    }

    const fingerTypeNames = allFingers.map(finger => {
      if (finger.includes('Pinky')) return 'Pinky';
      if (finger.includes('Ring')) return 'Ring';
      if (finger.includes('Middle')) return 'Middle';
      if (finger.includes('Pointing')) return 'Pointing';
      if (finger.includes('Thumb')) return 'Big';
      return 'Unknown';
    });

    const uniqueFingerNames = [...new Set(fingerTypeNames)];
    return uniqueFingerNames.join(', ');
  };

  const getHandsConfig = (): string => {
    const hasLeft = fingerConfig.activeFingersLeft.length > 0;
    const hasRight = fingerConfig.activeFingersRight.length > 0;

    if (hasLeft && hasRight) {
      return 'Both';
    } else if (hasLeft) {
      return 'Left';
    } else if (hasRight) {
      return 'Right';
    } else {
      return 'None';
    }
  };

  const getWordsConfig = (): string => {
    const { min, max } = fingerConfig.letterComplexity;

    if (min === max) {
      return `${max} let.`;
    } else {
      return `${min}-${max} let.`;
    }
  };

  // Generate calendar days
  const allDays = Array.from({ length: 30 }, (_, i) => getCalendarDay(i + 1));
  const rows = [
    allDays.slice(0, 10),   // Days 1-10
    allDays.slice(10, 20),  // Days 11-20
    allDays.slice(20, 30)   // Days 21-30
  ];

  return (
    <div className={`course-widget-unified ${className} ${customMode ? 'custom-mode' : ''}`}>
      {/* 🎯 FINAL CLEANUP: Enhanced Header with Mode Switching and Complexity */}
      <div className="course-header-enhanced">
        {/* Mode Switching Arrows */}
        <div className="mode-switching-container">
          <button
            className="mode-arrow-btn left-arrow"
            onClick={() => onModeChange?.(currentMode === 'oneWord' ? 'custom' : currentMode === 'course' ? 'oneWord' : 'course')}
            title={`Switch to ${currentMode === 'oneWord' ? 'Custom' : currentMode === 'course' ? 'One Word' : 'Course'} Training`}
          >
            ←
          </button>

          <div className="mode-title">
            {customMode ? (currentMode === 'oneWord' ? 'One Word Training' : 'Custom Training') : 'Course Training'}
          </div>

          <button
            className="mode-arrow-btn right-arrow"
            onClick={() => onModeChange?.(currentMode === 'custom' ? 'oneWord' : currentMode === 'oneWord' ? 'course' : 'custom')}
            title={`Switch to ${currentMode === 'custom' ? 'One Word' : currentMode === 'oneWord' ? 'Course' : 'Custom'} Training`}
          >
            →
          </button>
        </div>

        {/* Subtitle - Training and Round info for Course Mode */}
        {!customMode && (
          <div className="course-subtitle" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <button
              className="mode-arrow-btn"
              onClick={() => setViewLevel(Math.max(1, viewLevel - 1))}
              disabled={viewLevel <= 1}
              style={{ width: '24px', height: '24px', fontSize: '0.8rem', opacity: viewLevel <= 1 ? 0.3 : 1 }}
              title="Previous Level"
            >
              ←
            </button>
            Level {viewLevel}, Day {viewLevel === currentLevel ? currentDay : 1}, Round {viewLevel === currentLevel ? currentRound : 1}
            <button
              className="mode-arrow-btn"
              onClick={() => setViewLevel(viewLevel + 1)}
              // Disable right array if no data for viewLevel + 1 exists or it's locked
              disabled={!Array.isArray(courseProgress) && !courseProgress?.levelProgress?.[viewLevel + 1]?.unlocked}
              style={{ width: '24px', height: '24px', fontSize: '0.8rem', opacity: (!Array.isArray(courseProgress) && !courseProgress?.levelProgress?.[viewLevel + 1]?.unlocked) ? 0.3 : 1 }}
              title="Next Level"
            >
              →
            </button>
          </div>
        )}

        {/* Complexity Toggle - Only show in Custom Mode */}
        {customMode && onComplexityChange && (
          <div className="complexity-toggle-container">
            <span className="complexity-label">Complexity:</span>
            <ComplexityToggle
              value={complexityMode}
              onChange={onComplexityChange}
            />
          </div>
        )}
      </div>

      {currentMode === 'oneWord' ? (
        <div className="course-body-content">
          <div className="one-word-section" ref={dropdownRef}>
            <div className="one-word-input-row">
              <input
                type="text"
                className="one-word-input"
                value={oneWordValue}
                onChange={(e) => onOneWordChange?.(e.target.value.replace(/[^a-zA-Z ]/g, '').toLowerCase())}
                placeholder="Type a word or short phrase…"
                maxLength={40}
                autoFocus
              />
              <button
                type="button"
                className="one-word-dropdown-toggle"
                onClick={() => setDropdownOpen((o) => !o)}
                title="Choose a preset"
              >
                ▾
              </button>
              {dropdownOpen && (
                <div className="one-word-dropdown">
                  {ONE_WORD_PRESETS.map((w) => (
                    <button
                      key={w}
                      type="button"
                      className="one-word-dropdown-item"
                      onClick={() => {
                        onOneWordChange?.(w);
                        setDropdownOpen(false);
                      }}
                    >
                      {w}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="section-divider"></div>

          <div className="config-section">
            <div className="config-grid">
              <div className="config-item">
                <div className="config-label">Fingers:</div>
                <div className="config-value">{getFingerNames()}</div>
              </div>
              <div className="config-item">
                <div className="config-label">Hands:</div>
                <div className="config-value">{getHandsConfig()}</div>
              </div>
              <div className="config-item">
                <div className="config-label">Letters:</div>
                <div className="config-value">{oneWordValue.trim().length || '—'}</div>
              </div>
            </div>
          </div>

          <div className="section-divider"></div>
          <CourseDayStats
            currentDay={1}
            currentRound={1}
            courseProgress={[]}
            customStats={customStats}
            mode="custom"
            className="compact-stats"
          />

          <div className="start-training-section">
            <button
              onClick={() => {
                if (!isOneWordValid) return;
                onStartTraining();
              }}
              className="start-training-btn"
              disabled={!isOneWordValid}
              style={!isOneWordValid ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              🚀 Start Training
            </button>
          </div>
        </div>
      ) : (
        <div className="course-body-content">
          {/* Calendar Grid - Only show in course mode */}
          {!customMode && (
            <div className="calendar-grid">
              {rows.map((row, rowIndex) => (
                <div key={rowIndex} className="calendar-row">
                  {row.map(calendarDay => (
                    <button
                      key={calendarDay.dayNumber}
                      className={`calendar-day calendar-day--${calendarDay.state} ${calendarDay.dayNumber === currentDay ? 'calendar-day--active' : ''
                        } ${calendarDay.isSelectable ? 'calendar-day--selectable' : ''}`}
                      onClick={() => handleDayClick(calendarDay.dayNumber)}
                      disabled={!calendarDay.isSelectable}
                    >
                      {calendarDay.dayNumber}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Divider - Only show in course mode */}
          {!customMode && <div className="section-divider"></div>}

          {/* Rounds Section - Only show in course mode */}
          {!customMode && (
            <div className="rounds-section">
              <div className="rounds-grid">
                {[1, 2, 3, 4, 5].map(roundNumber => (
                  <button
                    key={roundNumber}
                    className={`round-button round-button--${getRoundState(roundNumber)} ${roundNumber === currentRound ? 'round-button--active' : ''
                      } ${isRoundSelectable(roundNumber) ? 'round-button--selectable' : ''}`}
                    onClick={() => handleRoundClick(roundNumber)}
                    disabled={!isRoundSelectable(roundNumber)}
                  >
                    {roundNumber}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Custom Mode Description - REMOVED as per user request (duplicate info) */}

          {/* Divider */}
          <div className="section-divider"></div>

          {/* Config Section */}
          <div className="config-section">
            <div className="config-grid">
              <div className="config-item">
                <div className="config-label">Fingers:</div>
                <div className="config-value">{getFingerNames()}</div>
              </div>
              <div className="config-item">
                <div className="config-label">Hands:</div>
                <div className="config-value">{getHandsConfig()}</div>
              </div>
              <div className="config-item">
                <div className="config-label">Words:</div>
                <div className="config-value">{getWordsConfig()}</div>
              </div>
            </div>
          </div>

          {/* Statistics Widget - Show for both modes */}
          <div className="section-divider"></div>
          <CourseDayStats
            currentDay={currentDay}
            currentRound={currentRound}
            courseProgress={effectiveCourseProgress}
            customStats={customStats}
            mode={customMode ? 'custom' : 'course'}
            className="compact-stats"
          />

          {/* Start Training Button */}
          <div className="start-training-section">
            <button
              onClick={() => {
                if (!customMode && !isAuthenticated && onRequestLogin) {
                  onRequestLogin();
                } else {
                  onStartTraining();
                }
              }}
              className="start-training-btn"
            >
              🚀 Start Training
            </button>
          </div>
        </div>
      )}

      <style>{`
        .course-widget-unified {
          width: 100%;
          height: 100%;
          background: rgba(17, 24, 39, 0.8);
          border: 2px solid #3b82f6;
          border-radius: 12px;
          padding: 16px;
          backdrop-filter: blur(10px);
          display: flex;
          flex-direction: column;
          gap: 8px;
          box-sizing: border-box;
        }

        .course-header {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
          text-align: center;
          margin-bottom: 8px;
          flex: 0 0 auto;
        }

        .calendar-grid {
          display: flex;
          flex-direction: column;
          gap: 6px;
          flex: 0 0 auto;
        }

        .calendar-row {
          display: flex;
          gap: 6px;
          justify-content: center;
        }

        .calendar-day {
          width: 28px;
          height: 28px;
          border: 2px solid transparent;
          border-radius: 6px;
          background: rgba(75, 85, 99, 0.3);
          color: #9ca3af;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: default;
          transition: all 0.2s ease-out;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .calendar-day--selectable {
          cursor: pointer;
        }

        .calendar-day--selectable:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        .calendar-day--locked {
          background: rgba(75, 85, 99, 0.2);
          color: #4b5563;
          cursor: not-allowed;
        }

        .calendar-day--completed {
          background: #22c55e;
          color: white;
          border-color: #16a34a;
        }

        .calendar-day--unlocked {
          background: #3b82f6;
          color: white;
          border-color: #2563eb;
        }

        .calendar-day--current {
          background: rgba(75, 85, 99, 0.5);
          color: #d1d5db;
          border-color: rgba(156, 163, 175, 0.5);
        }

        .calendar-day--active {
          background: #3b82f6 !important;
          color: white !important;
          border-color: #2563eb !important;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
        }

        .section-divider {
          height: 1px;
          background: rgba(75, 85, 99, 0.3);
          margin: 2px 0;
        }

        .rounds-section {
          display: flex;
          flex-direction: column;
          gap: 8px;
          flex: 0 0 auto;
        }

        .round-header {
          font-size: 1rem;
          font-weight: 600;
          color: #e5e7eb;
          text-align: center;
        }

        .rounds-grid {
          display: flex;
          gap: 8px;
          justify-content: center;
        }

        .round-button {
          width: 40px;
          height: 40px;
          border: 2px solid transparent;
          border-radius: 8px;
          background: rgba(75, 85, 99, 0.3);
          color: #6b7280;
          font-size: 0.875rem;
          font-weight: 700;
          cursor: default;
          transition: all 0.2s ease-out;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .round-button--selectable {
          cursor: pointer;
        }

        .round-button--selectable:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        .round-button--completed {
          background: #22c55e;
          color: white;
          border-color: #16a34a;
        }

        .round-button--current {
          background: rgba(75, 85, 99, 0.5);
          color: #d1d5db;
          border-color: rgba(156, 163, 175, 0.5);
        }

        .round-button--active {
          background: #3b82f6 !important;
          color: white !important;
          border-color: #2563eb !important;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
        }

        .round-button--locked {
          background: rgba(75, 85, 99, 0.2);
          color: #4b5563;
          cursor: not-allowed;
        }

        .config-section {
          flex: 0 0 auto;
        }

        .config-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 8px;
          background: rgba(17, 24, 39, 0.6);
          border: 1px solid rgba(75, 85, 99, 0.3);
          border-radius: 8px;
          padding: 12px;
        }

        .config-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .config-label {
          font-size: 0.75rem;
          color: #9ca3af;
          font-weight: 500;
          margin-bottom: 2px;
        }

        .config-value {
          font-size: 0.875rem;
          color: #e5e7eb;
          font-weight: 600;
        }

        .start-training-section {
          margin-top: auto;
          padding-top: 6px;
          flex: 0 0 auto;
        }

        .start-training-btn {
          width: 100%;
          padding: 12px 16px;
          background: linear-gradient(45deg, #3b82f6, #2563eb);
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease-out;
        }

        .start-training-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
        }

        /* Compact stats styling for tight integration */
        .course-widget-unified .compact-stats {
          margin: 0;
        }

        .course-widget-unified .compact-stats .course-day-stats {
          padding: 4px;
          margin: 0;
        }

        .course-widget-unified .compact-stats .stats-grid {
          gap: 3px;
        }

        .course-widget-unified .compact-stats .stat-item {
          min-height: 24px;
          padding: 2px 1px;
        }

        .course-widget-unified .compact-stats .stat-label {
          font-size: 0.55rem;
        }

        .course-widget-unified .compact-stats .stat-value {
          font-size: 0.65rem;
        }

        /* Custom mode button - purple like border */
        .course-widget-unified.custom-mode .start-training-btn {
          background: linear-gradient(45deg, #8b5cf6, #7c3aed);
        }

        .course-widget-unified.custom-mode .start-training-btn:hover {
          box-shadow: 0 8px 20px rgba(139, 92, 246, 0.4);
        }

        /* Custom description removed - was duplicate info */

        .course-widget-unified.custom-mode {
          border-color: #8b5cf6;
        }

        .course-widget-unified.custom-mode .course-header {
          color: #8b5cf6;
        }

        /* 🎯 FINAL CLEANUP: Enhanced Header Styles */
        .course-header-enhanced {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 8px;
          flex: 0 0 auto;
        }

        .mode-switching-container {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          background: rgba(17, 24, 39, 0.6);
          border: 1px solid rgba(75, 85, 99, 0.3);
          border-radius: 8px;
          padding: 8px 12px;
        }

        .mode-arrow-btn {
          width: 32px;
          height: 32px;
          border: 1px solid rgba(75, 85, 99, 0.5);
          border-radius: 6px;
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.1s ease; /* Faster animation */
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mode-arrow-btn:hover {
          background: rgba(59, 130, 246, 0.2);
          border-color: rgba(59, 130, 246, 0.6);
          transform: translateY(-1px);
        }

        .mode-arrow-btn:active {
          transform: translateY(0); /* Remove active state */
        }

        .course-widget-unified.custom-mode .mode-arrow-btn {
          background: rgba(139, 92, 246, 0.1);
          color: #8b5cf6;
          border-color: rgba(139, 92, 246, 0.5);
        }

        .course-widget-unified.custom-mode .mode-arrow-btn:hover {
          background: rgba(139, 92, 246, 0.2);
          border-color: rgba(139, 92, 246, 0.6);
        }

        .course-widget-unified.custom-mode .mode-arrow-btn:active {
          transform: translateY(0); /* Remove active state */
        }

        .mode-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #ffffff;
          text-align: center;
          flex: 1;
        }

        .course-widget-unified.custom-mode .mode-title {
          color: #8b5cf6;
        }

        .course-subtitle {
          font-size: 1rem;
          font-weight: 600;
          color: #e5e7eb;
          text-align: center;
          background: rgba(17, 24, 39, 0.6);
          border: 1px solid rgba(75, 85, 99, 0.3);
          border-radius: 6px;
          padding: 6px 12px;
        }

        .complexity-toggle-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          background: rgba(17, 24, 39, 0.6);
          border: 1px solid rgba(75, 85, 99, 0.3);
          border-radius: 6px;
          padding: 8px 12px;
        }

        .complexity-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #e5e7eb;
        }

        /* Adjust rounds section spacing for new layout */
        .rounds-section {
          display: flex;
          flex-direction: column;
          gap: 6px;
          flex: 0 0 auto;
        }

        .rounds-grid {
          display: flex;
          gap: 6px;
          justify-content: center;
        }

        .round-button {
          width: 36px;
          height: 36px;
          border: 2px solid transparent;
          border-radius: 6px;
          background: rgba(75, 85, 99, 0.3);
          color: #6b7280;
          font-size: 0.8rem;
          font-weight: 700;
          cursor: default;
          transition: all 0.2s ease-out;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .round-button--selectable {
          cursor: pointer;
        }

        .round-button--selectable:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        .round-button--completed {
          background: #22c55e;
          color: white;
          border-color: #16a34a;
        }

        .round-button--current {
          background: rgba(75, 85, 99, 0.5);
          color: #d1d5db;
          border-color: rgba(156, 163, 175, 0.5);
        }

        .round-button--active {
          background: #3b82f6 !important;
          color: white !important;
          border-color: #2563eb !important;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
        }

        .round-button--locked {
          background: rgba(75, 85, 99, 0.2);
          color: #4b5563;
          cursor: not-allowed;
        }

        /* Hide old header since we're using enhanced header */
        .course-widget-unified .course-header {
          display: none;
        }

        /* One Word mode */
        .one-word-section {
          position: relative;
        }
        .one-word-input-row {
          display: flex;
          align-items: stretch;
          background: rgba(17, 24, 39, 0.6);
          border: 1px solid rgba(75, 85, 99, 0.5);
          border-radius: 8px;
          overflow: hidden;
        }
        .one-word-input-row:focus-within {
          border-color: #8b5cf6;
        }
        .one-word-input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          padding: 10px 14px;
          color: #e5e7eb;
          font-size: 1rem;
          letter-spacing: 0.04em;
        }
        .one-word-input::placeholder {
          color: #6b7280;
        }
        .one-word-dropdown-toggle {
          width: 36px;
          border: none;
          border-left: 1px solid rgba(75, 85, 99, 0.5);
          background: rgba(139, 92, 246, 0.1);
          color: #8b5cf6;
          font-size: 1rem;
          cursor: pointer;
        }
        .one-word-dropdown-toggle:hover {
          background: rgba(139, 92, 246, 0.2);
        }
        .one-word-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          max-height: 220px;
          overflow-y: auto;
          background: rgba(17, 24, 39, 0.98);
          border: 1px solid rgba(139, 92, 246, 0.5);
          border-radius: 8px;
          z-index: 50;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2px;
          padding: 4px;
        }
        .one-word-dropdown-item {
          background: rgba(75, 85, 99, 0.25);
          border: none;
          color: #e5e7eb;
          padding: 6px 8px;
          border-radius: 4px;
          font-size: 0.85rem;
          cursor: pointer;
          text-align: left;
        }
        .one-word-dropdown-item:hover {
          background: rgba(139, 92, 246, 0.3);
        }
      `}</style>
    </div>
  );
};

export default UnifiedCourseWidget;
